/**
 * Recruiter Copilot — REST surface.
 *
 * Goes through the shared axios instance, so the Authorization header and the
 * CSRF token for state-changing calls are attached by the existing
 * interceptors. `/copilot/status` declares no authentication classes, so the
 * header it receives is ignored rather than rejected — it is safe on the
 * shared client.
 */

import instance, { baseURL } from "../api/instance";
import { COPILOT_REST, NEW_SESSION, TRANSCRIPT_PAGE_LIMIT } from "./constants";

/** Normalised failure shape, so callers never have to unpack an axios error. */
export class CopilotApiError extends Error {
  constructor(message, { status = null, code = null, cause = null } = {}) {
    super(message);
    this.name = "CopilotApiError";
    this.status = status;
    this.code = code;
    this.cause = cause;
  }

  /** Out of scope and never-existed are deliberately indistinguishable (404). */
  get isNotFound() {
    return this.status === 404;
  }
}

/** Pull the most useful message out of a DRF error body without leaking internals. */
function messageFrom(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.length < 200) return data;
  if (data && typeof data === "object") {
    const candidate = data.detail || data.message || data.error;
    if (typeof candidate === "string") return candidate;
  }
  return fallback;
}

async function request(promise, fallbackMessage) {
  try {
    const { data } = await promise;
    return data;
  } catch (error) {
    throw new CopilotApiError(messageFrom(error, fallbackMessage), {
      status: error?.response?.status ?? null,
      code: error?.code ?? null,
      cause: error,
    });
  }
}

/**
 * Feature detection. Call before showing any entry point.
 * Returns { configured, enabled, protocol_version, token_budget,
 * max_concurrent_runs, ask_timeout_seconds, socket_ticket_ttl_seconds }.
 */
export const fetchCopilotStatus = () =>
  request(
    instance.get(COPILOT_REST.status),
    "Could not check whether the copilot is available."
  );

/**
 * Mint a single-purpose socket ticket. Short-lived and NOT cached: the TTL is
 * read from the response rather than assumed, because the guide quotes both
 * 60s and 120s in different places.
 */
export const mintCopilotTicket = () =>
  request(
    instance.post(COPILOT_REST.ticket),
    "Could not start Hairo. Please try again."
  );

/**
 * Session list for the history sidebar. Only sessions with a completed turn
 * appear. Returns `{ sessions, next_offset }`; `next_offset` is null on the
 * last page.
 */
export const listCopilotSessions = (limit = 25, offset = 0) =>
  request(
    instance.get(COPILOT_REST.sessions, {
      params: offset ? { limit, offset } : { limit },
    }),
    "Could not load your copilot conversations."
  );

/**
 * Full transcript, with gates/records/cost per message.
 * Paginated backwards: pass the previous response's next_before_sequence.
 */
export const fetchCopilotSession = (
  sessionId,
  { limit = TRANSCRIPT_PAGE_LIMIT, beforeSequence = null } = {}
) => {
  const params = { limit };
  if (beforeSequence !== null && beforeSequence !== undefined) {
    params.before_sequence = beforeSequence;
  }
  return request(
    instance.get(COPILOT_REST.session(sessionId), { params }),
    "Could not load this conversation."
  );
};

/** Soft delete — hidden from every read path, retained for audit. */
export const deleteCopilotSession = (sessionId) =>
  request(
    instance.delete(COPILOT_REST.sessionDelete(sessionId)),
    "Could not delete this conversation."
  );

/**
 * What a citation chip opens, and what the reasoning panel expands.
 * `fields` is the same projection the model was given — no more.
 */
export const fetchCopilotRecord = (kind, recordId, sessionId = null) =>
  request(
    instance.get(COPILOT_REST.record(kind, recordId), {
      // With the session, `fields` also carries what the answer used (e.g. the
      // technical score it ranked by), named in `focus`.
      params: sessionId && sessionId !== NEW_SESSION ? { session: sessionId } : undefined,
    }),
    "Could not open that record."
  );

/**
 * Signed, five-minute link to a rendered report, scoped to one message.
 * Needed because window.open cannot send an Authorization header.
 */
export const createCopilotReportLink = (messageId) =>
  request(
    instance.post(COPILOT_REST.reportLink(messageId)),
    "Could not open the report."
  );

/**
 * A short-lived download link for a document. `key` names a document card on
 * the message; without it the answer itself is the document.
 * @param {string} messageId
 * @param {{ format: "pdf"|"docx"|"xlsx", key?: string|null }} options
 */
export const createCopilotDocumentLink = (messageId, { format, key = null }) =>
  request(
    instance.post(COPILOT_REST.documentLink(messageId), { format, key: key || "" }),
    "Could not prepare the download."
  );

/** Join an API path onto the configured backend origin. */
export const absoluteCopilotUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  try {
    return new URL(path.replace(/^\//, ""), baseURL).toString();
  } catch {
    return `${baseURL.replace(/\/+$/, "")}/${path.replace(/^\//, "")}`;
  }
};

/** Join the relative report URL onto the API origin. */
export const resolveReportUrl = (relativeUrl) => absoluteCopilotUrl(relativeUrl);

/** The server reads the whole file before answering; a scanned PDF can take ~30s. */
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Attach one file to a conversation. Resolves only once the server has read
 * it: `{ file: { id, name, kind, size, status, error, notes }, files: limits }`.
 * A file the server stored but could not read comes back as a CopilotApiError
 * with status 422 and the reason as its message.
 */
export const uploadCopilotFile = (sessionId, file, { onProgress, signal } = {}) => {
  const form = new FormData();
  form.append("file", file);
  return request(
    instance.post(COPILOT_REST.sessionFiles(sessionId), form, {
      timeout: UPLOAD_TIMEOUT_MS,
      signal,
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      },
    }),
    "Could not attach this file."
  );
};

/** Remove an attached file (its original and its text are deleted server-side). */
export const deleteCopilotFile = (fileId) =>
  request(instance.delete(COPILOT_REST.file(fileId)), "Could not remove this file.");

/** Idempotent: a user can change up to down freely. */
export const sendCopilotFeedback = (messageId, verdict, reason = "") =>
  request(
    instance.post(COPILOT_REST.feedback(messageId), { verdict, reason }),
    "Could not record your feedback."
  );
